import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { PdMultiSelectDialogComponent } from "@shared/dialogs/pd-multi-select-dialog/pd-multi-select-dialog.component";
import { VisualizationService } from "@shared/services/visualization.service";
import { X_AXIS_TYPE } from "@shared/enums/x-axis-type.enum";
import { HelperService } from "@shared/services/helper.service";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";

@Component({
  selector: 'app-performance-development-wrapper',
  templateUrl: './performance-development-wrapper.component.html',
  styleUrls: ['./performance-development-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class PerformanceDevelopmentWrapperComponent implements OnInit {
  private dialog: MatDialog = inject(MatDialog);
  private visualizationService: VisualizationService = inject(VisualizationService);
  private helperService: HelperService = inject(HelperService);

  public yAxis1: string[] = [];
  public yAxis2: string[] = [];
  public yAxis3: string[] = [];
  public type: string;
  public plotConfig: any;
  public xAxisType: string;
  public copyOptionList: any[];

  @Input() public yAxisConfig: any;
  @Input() public id: any;
  @Input() public optionList: any;
  @Input() public isRemovable: boolean;
  @Input() public simulationType: number;
  @Input() public primaryType: string | undefined;
  @Input() public secondaryType: string | undefined;
  @Input() public sportId: number;

  @Output() public onWrapperChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onChartTypeChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onRemoveGraph: EventEmitter<any> = new EventEmitter<any>();

  public ngOnInit(): void {
    this.setXAxisType(this.yAxisConfig.x_axis_configuration.ticks);
    this.setCurrentPerformanceDevelopmentOrder(this.yAxisConfig.name);
    this.generateChart(this.xAxisType);
  }

  public setXAxisType(type: string): void {
    if (type === X_AXIS_TYPE.DATE) {
      this.xAxisType = X_AXIS_TYPE.DATETIME;
    } else {
      this.xAxisType = X_AXIS_TYPE.DATETIME;
    }
  }

  public setCurrentPerformanceDevelopmentOrder(name: string): void {
    if (!this.visualizationService.isExistsGraphItemInList(name)) {
      this.visualizationService.setPerformanceDevelopmentOrder(name);
    }
  }

  public updateColorForLine(dataList: any[], optionList: any[]): void {
    dataList.forEach((data: any) => {
      optionList.forEach((item: any) => {
        if (data.id === item.id && !data.hasOwnProperty('color')) {
          data.color = item.color;
        }
      });
    });
  }

  public generateChart(type: string): void {
    this.copyOptionList = this.helperService.makeCopy(this.optionList);
    this.type = type;
    this.yAxis1 = this.yAxisConfig.y_axis_configuration.left_1.data;
    this.yAxis2 = this.yAxisConfig.y_axis_configuration.left_2.data;
    this.yAxis3 = this.yAxisConfig.y_axis_configuration.right_1.data;
    this.updateColorForLine(this.yAxis1, this.copyOptionList);
    this.updateColorForLine(this.yAxis2, this.copyOptionList);
    this.updateColorForLine(this.yAxis3, this.copyOptionList);
    this.plotConfig = this.visualizationService.generateChartConfig(
      this.yAxis1,
      this.yAxis2,
      this.yAxis3,
      this.simulationType,
      this.primaryType,
      this.secondaryType,
      type,
      );
  }

  public onToggleChanged(event: string): void {
    this.generateChart(event);
    this.onChartTypeChanged.emit({
      id: this.id,
      type: event,
    });
  }

  public setCurrentDataSet(yAxisList: any[], sportId: number, metricOptionList: any[]): any {
    yAxisList.forEach((yAxis: any) => {
      metricOptionList.forEach((metric: any) => {
        if (yAxis.id === metric.id) {
          if (metric.hasOwnProperty('data')) {
            metric.data.forEach((item: any) => {
              if (item.sport_id_list.includes(sportId)) {
                yAxis.current_data_set = item.data_set;
              }
            });
          }
        }
      });
    });

    return yAxisList;
  }

  public openYAxis1(): void {
    const dialogRef: MatDialogRef<PdMultiSelectDialogComponent> = this.dialog.open(PdMultiSelectDialogComponent, {
      width: '800px',
      data: {
        dataPointList: this.yAxis1,
        optionList: this.helperService.makeCopy(this.optionList),
        title: 'Y-axis 1',
        yAxisOrder: 1,
      },
      disableClose: true,
    });
    dialogRef.afterClosed()
      .subscribe({
        next: (yAxisList: any) => {
          this.yAxis1 = this.setCurrentDataSet(yAxisList, this.sportId, this.optionList);
          this.plotConfig = this.visualizationService.generateChartConfig(
            this.yAxis1,
            this.yAxis2,
            this.yAxis3,
            this.simulationType,
            this.primaryType,
            this.secondaryType,
            this.type,
          );
          this.onWrapperChanged.emit({
            id: this.id,
            yAxis1: this.yAxis1,
            yAxis2: this.yAxis2,
            yAxis3: this.yAxis3,
          });
        },
        error: (error: any) => {
          console.log(error);
        },
      })
  }

  public openYAxis2(): void {
    const dialogRef: MatDialogRef<PdMultiSelectDialogComponent> = this.dialog.open(PdMultiSelectDialogComponent, {
      width: '800px',
      data: {
        dataPointList: this.yAxis2,
        optionList: this.helperService.makeCopy(this.optionList),
        title: 'Y-axis 2',
        yAxisOrder: 2,
      },
      disableClose: true,
    });
    dialogRef.afterClosed()
      .subscribe({
        next: (yAxisList: any) => {
          if (yAxisList) {
            this.yAxis2 = this.setCurrentDataSet(yAxisList, this.sportId, this.optionList);
            this.plotConfig = this.visualizationService.generateChartConfig(
              this.yAxis1,
              this.yAxis2,
              this.yAxis3,
              this.simulationType,
              this.primaryType,
              this.secondaryType,
              this.type,
            );
            this.onWrapperChanged.emit({
              id: this.id,
              yAxis1: this.yAxis1,
              yAxis2: this.yAxis2,
              yAxis3: this.yAxis3,
            });
          }
        },
        error: (error: any) => {
          console.log(error);
        },
      })
  }

  public onYAxis3Changed(): void {
    const dialogRef: MatDialogRef<PdMultiSelectDialogComponent> = this.dialog.open(PdMultiSelectDialogComponent, {
      width: '800px',
      data: {
        dataPointList: this.yAxis3,
        optionList: this.helperService.makeCopy(this.optionList),
        title: 'Y-axis 3',
        yAxisOrder: 3,
      },
      disableClose: true,
    });
    dialogRef.afterClosed()
      .subscribe({
        next: (yAxisList: any) => {
          if (yAxisList) {
            this.yAxis3 = this.setCurrentDataSet(yAxisList, this.sportId, this.optionList);
            this.plotConfig = this.visualizationService.generateChartConfig(
              this.yAxis1,
              this.yAxis2,
              this.yAxis3,
              this.simulationType,
              this.primaryType,
              this.secondaryType,
              this.type,
            );
            this.onWrapperChanged.emit({
              id: this.id,
              yAxis1: this.yAxis1,
              yAxis2: this.yAxis2,
              yAxis3: this.yAxis3,
            });
          }
        },
        error: (error: any) => {
          console.log(error);
        },
      })
  }

  public removeCurrentGraph(): void {
    this.onRemoveGraph.emit(this.id);
  }
}
